import React, { useState, useEffect } from "react";
import { 
  FaUserCircle, FaKey, FaLock, FaGlobe, FaPalette, FaHistory, 
  FaCheckCircle, FaArrowLeft, FaPen, FaCamera, FaChevronRight,
  FaSearch, FaBell, FaKeyboard, FaShieldAlt, FaQuestionCircle,
  FaSignOutAlt, FaVideo, FaCog, FaStar, FaFileContract, FaInfoCircle, FaUsers,
  FaDownload, FaMicrophone, FaMobileAlt, FaTrashAlt, FaCheck, FaBan, FaFingerprint,
  FaArchive
} from "react-icons/fa";
import { auth, db } from "../firebase";
import { signOut } from "firebase/auth";
import { ref, onValue, update } from "firebase/database";

function SettingsList({ currentUser }) {
  const [profile, setProfile] = useState(null);
  const [view, setView] = useState("main"); 
  const [editName, setEditName] = useState("");
  const [editBio, setEditBio] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Additional Subview States
  const [lang, setLang] = useState(localStorage.getItem("app_language") || "English");
  const [pinCode, setPinCode] = useState("");
  const [isPinEnabled, setIsPinEnabled] = useState(localStorage.getItem("twostep_enabled") === "true");
  const [newPhone, setNewPhone] = useState("");
  const [selectedWallpaper, setSelectedWallpaper] = useState(localStorage.getItem("chat_wallpaper") || "#0b141a");

  // Local Storage Settings
  const [sounds, setSounds] = useState(true);
  const [desktopAlerts, setDesktopAlerts] = useState(true);
  const [showPreviews, setShowPreviews] = useState(true);
  const [enterIsSend, setEnterIsSend] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    const profileRef = ref(db, `users/${currentUser.uid}`);
    const unsubscribe = onValue(profileRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setProfile(data);
        setEditName(data.name || currentUser.displayName || "");
        setEditBio(data.bio || "");
      }
    });

    // Load Local Storage
    setSounds(localStorage.getItem("settings_sounds") !== "false");
    setDesktopAlerts(localStorage.getItem("settings_alerts") !== "false");
    setShowPreviews(localStorage.getItem("settings_previews") !== "false");
    setEnterIsSend(localStorage.getItem("settings_enter_send") === "true");

    return () => unsubscribe();
  }, [currentUser]);

  const handleUpdateProfile = () => {
    if (!currentUser) return;
    update(ref(db, `users/${currentUser.uid}`), {
      name: editName,
      bio: editBio
    });
    setView("main");
  };

  const updateFirebaseSetting = (category, key, value) => {
    if (!currentUser) return;
    update(ref(db, `users/${currentUser.uid}/settings/${category}`), {
      [key]: value
    });
  };

  const toggleLocalSetting = (key, state, setState) => {
    const newVal = !state;
    setState(newVal);
    localStorage.setItem(key, newVal.toString());
  };

  const handleLogout = () => {
    signOut(auth).then(() => {
      window.location.href = "/login";
    });
  };

  const handleSavePin = () => {
    if (pinCode.length < 6) {
      alert("PIN must be 6 digits!");
      return;
    }
    setIsPinEnabled(true);
    localStorage.setItem("twostep_enabled", "true");
    localStorage.setItem("twostep_pin", pinCode);
    alert("Two-step verification enabled successfully!");
    setView("account");
  };

  const handleChangeLanguage = (newLang) => {
    setLang(newLang);
    localStorage.setItem("app_language", newLang);
    alert(`Language changed to ${newLang}`);
  };

  const handleSelectWallpaper = (color) => {
    setSelectedWallpaper(color);
    localStorage.setItem("chat_wallpaper", color);
    document.documentElement.style.setProperty("--chat-area-bg", color);
    alert("Chat wallpaper updated!");
  };

  /* ===============================
     REUSABLE UI COMPONENTS
  =============================== */
  const SettingsHeader = ({ profile, currentUser, onClick }) => (
    <div className="settings-header-card" onClick={onClick}>
      <div className="avatar">
        {profile?.photoURL || currentUser?.photoURL ? 
          <img src={profile?.photoURL || currentUser.photoURL} alt="p" /> : 
          <FaUserCircle style={{ fontSize: '80px', color: '#888' }} />
        }
      </div>
      <div className="settings-header-info">
        <h3>{profile?.name || currentUser?.displayName || "User"}</h3>
        <p>{profile?.bio || "Your profile information"}</p>
      </div>
    </div>
  );

  const SettingsSearch = () => (
    <div className="sidebar-search-container">
      <div className="search-input-box">
        <FaSearch className="search-icon" />
        <input
          type="text"
          placeholder="Search settings..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
    </div>
  );

  const SettingsItem = ({ icon, label, description, onClick, type = "normal", rightElement = null }) => (
    <div className={`settings-item ${type === 'logout' ? 'logout-row' : ''} ${type === 'danger' ? 'danger-row' : ''}`} onClick={onClick}>
      <div className="settings-item-icon">{icon}</div>
      <div className="settings-item-content">
        <div>
          <h4>{label}</h4>
          {description && <p>{description}</p>}
        </div>
        {rightElement ? rightElement : (type !== 'logout' && type !== 'danger' && <FaChevronRight style={{ fontSize: '10px', color: '#888' }} />)}
      </div>
    </div>
  );

  const ToggleSwitch = ({ checked, onChange }) => (
    <label className="settings-toggle">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="settings-slider round"></span>
    </label>
  );

  /* ===============================
     VIEWS
  =============================== */
  const renderMain = () => {
    const categories = [
      { id: 'general', icon: <FaCog />, label: "General", desc: "App preferences" },
      { id: 'account', icon: <FaKey />, label: "Account", desc: "Security, email, phone number" },
      { id: 'privacy', icon: <FaLock />, label: "Privacy", desc: "Last seen, online, blocked contacts" },
      { id: 'chats', icon: <FaPalette />, label: "Chats", desc: "Theme, wallpaper, enter key" },
      { id: 'video', icon: <FaVideo />, label: "Video & Voice", desc: "Camera, microphone" },
      { id: 'notifications', icon: <FaBell />, label: "Notifications", desc: "Sounds, desktop alerts" },
      { id: 'shortcuts', icon: <FaKeyboard />, label: "Keyboard shortcuts", desc: "Application shortcuts" },
      { id: 'help', icon: <FaQuestionCircle />, label: "Help", desc: "FAQ, feedback, updates" },
    ];

    const filteredCategories = categories.filter((cat) =>
      cat.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cat.desc.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
      <div className="settings-view">
        <div className="sidebar-header">
          <h2>Settings</h2>
        </div>
        <SettingsSearch />
        <div className="settings-item-list">
          {!searchQuery && (
            <SettingsHeader profile={profile} currentUser={currentUser} onClick={() => setView("profile")} />
          )}
          {filteredCategories.map((cat) => (
            <SettingsItem 
              key={cat.id} icon={cat.icon} label={cat.label} description={cat.desc} 
              onClick={() => setView(cat.id)}
            />
          ))}
          <SettingsItem icon={<FaSignOutAlt />} label="Log out" type="logout" onClick={handleLogout} />
        </div>
      </div>
    );
  };

  const renderProfile = () => (
    <div className="settings-subview">
      <div className="back-header">
        <FaArrowLeft className="back-icon" onClick={() => setView("main")} />
        <h2>Profile</h2>
      </div>
      <div className="settings-content" style={{ padding: '30px 20px' }}>
        <div className="profile-edit-section" style={{ textAlign: 'center', marginBottom: '30px' }}>
          <div className="avatar-wrapper" style={{ position: 'relative', display: 'inline-block' }}>
            <div className="avatar" style={{ width: '100px', height: '100px' }}>
              {profile?.photoURL || currentUser?.photoURL ? 
                <img src={profile?.photoURL || currentUser.photoURL} alt="p" /> : 
                <FaUserCircle style={{ fontSize: '100px' }} />
              }
            </div>
            <div className="camera-overlay"><FaCamera /></div>
          </div>
        </div>

        <div className="input-group" style={{ marginBottom: '20px' }}>
          <label style={{ color: '#00a884', fontSize: '13px' }}>Your Name</label>
          <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #00a884', padding: '5px 0' }}>
            <input 
              type="text" value={editName} onChange={(e) => setEditName(e.target.value)}
              style={{ flex: 1, background: 'transparent', border: 'none', color: 'white', outline: 'none' }}
            />
            <FaPen style={{ fontSize: '12px', color: '#888' }} />
          </div>
        </div>

        <div className="input-group">
          <label style={{ color: '#00a884', fontSize: '13px' }}>About</label>
          <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #333', padding: '5px 0' }}>
            <input 
              type="text" value={editBio} onChange={(e) => setEditBio(e.target.value)}
              style={{ flex: 1, background: 'transparent', border: 'none', color: 'white', outline: 'none' }}
            />
            <FaPen style={{ fontSize: '12px', color: '#888' }} />
          </div>
        </div>

        <button 
          onClick={handleUpdateProfile}
          style={{ width: '100%', marginTop: '30px', background: '#00a884', color: 'white', border: 'none', padding: '12px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
        >
          Save Changes
        </button>
      </div>
    </div>
  );

  const renderAccount = () => (
    <div className="settings-subview">
      <div className="back-header">
        <FaArrowLeft className="back-icon" onClick={() => setView("main")} />
        <h2>Account</h2>
      </div>
      <div className="settings-content">
        <SettingsItem icon={<FaShieldAlt />} label="Security notifications" onClick={() => setView("security")} />
        <SettingsItem icon={<FaFingerprint />} label="Two-step verification" onClick={() => setView("twostep")} />
        <SettingsItem icon={<FaMobileAlt />} label="Change number" onClick={() => setView("changenumber")} />
        <SettingsItem icon={<FaFileContract />} label="Request account info" onClick={() => alert("Account report requested! Download will be available in 3 days.")} />
        <SettingsItem icon={<FaTrashAlt style={{color: '#f15c6d'}} />} label="Delete account" type="danger" onClick={() => alert("Delete account functionality disabled in prototype.")} />
      </div>
    </div>
  );

  const renderPrivacy = () => {
    const privacySettings = profile?.settings?.privacy || {};
    return (
      <div className="settings-subview">
        <div className="back-header">
          <FaArrowLeft className="back-icon" onClick={() => setView("main")} />
          <h2>Privacy</h2>
        </div>
        <div className="settings-content" style={{ padding: '10px 20px' }}>
          <p style={{ color: '#8696a0', fontSize: '13px', paddingBottom: '10px', borderBottom: '1px solid var(--border-light)' }}>Who can see my personal info</p>
          
          <div className="settings-row-select">
            <div>
              <h4>Last seen and online</h4>
              <p>{privacySettings.lastSeen || "Everyone"}</p>
            </div>
            <select value={privacySettings.lastSeen || "Everyone"} onChange={(e) => updateFirebaseSetting('privacy', 'lastSeen', e.target.value)}>
              <option value="Everyone">Everyone</option>
              <option value="My Contacts">My Contacts</option>
              <option value="Nobody">Nobody</option>
            </select>
          </div>

          <div className="settings-row-select">
            <div>
              <h4>Profile photo</h4>
              <p>{privacySettings.profilePhoto || "Everyone"}</p>
            </div>
            <select value={privacySettings.profilePhoto || "Everyone"} onChange={(e) => updateFirebaseSetting('privacy', 'profilePhoto', e.target.value)}>
              <option value="Everyone">Everyone</option>
              <option value="My Contacts">My Contacts</option>
              <option value="Nobody">Nobody</option>
            </select>
          </div>

          <div className="settings-row-select">
            <div>
              <h4>About</h4>
              <p>{privacySettings.about || "Everyone"}</p>
            </div>
            <select value={privacySettings.about || "Everyone"} onChange={(e) => updateFirebaseSetting('privacy', 'about', e.target.value)}>
              <option value="Everyone">Everyone</option>
              <option value="My Contacts">My Contacts</option>
              <option value="Nobody">Nobody</option>
            </select>
          </div>

          <div className="settings-row-toggle" style={{ marginTop: '20px', borderTop: '1px solid var(--border-light)', paddingTop: '20px' }}>
            <div>
              <h4>Read receipts</h4>
              <p>If turned off, you won't send or receive read receipts.</p>
            </div>
            <ToggleSwitch 
              checked={privacySettings.readReceipts !== false} 
              onChange={(e) => updateFirebaseSetting('privacy', 'readReceipts', e.target.checked)} 
            />
          </div>

          <div className="settings-row-select" style={{ marginTop: '20px', borderTop: '1px solid var(--border-light)', paddingTop: '20px' }}>
            <div>
              <h4>Blocked contacts</h4>
              <p>0</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderChats = () => {
    const chatSettings = profile?.settings?.chats || {};
    return (
      <div className="settings-subview">
        <div className="back-header">
          <FaArrowLeft className="back-icon" onClick={() => setView("main")} />
          <h2>Chats</h2>
        </div>
        <div className="settings-content" style={{ padding: '20px' }}>
          <div className="settings-row-select">
            <div>
              <h4>Theme</h4>
              <p>{chatSettings.theme || "System default"}</p>
            </div>
            <select value={chatSettings.theme || "system"} onChange={(e) => updateFirebaseSetting('chats', 'theme', e.target.value)}>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
              <option value="system">System default</option>
            </select>
          </div>

          <div className="settings-row-select" style={{ marginTop: '20px', cursor: 'pointer' }} onClick={() => setView("wallpaper")}>
            <div>
              <h4>Chat wallpaper</h4>
            </div>
            <FaChevronRight style={{ fontSize: '10px', color: '#888' }} />
          </div>

          <div className="settings-row-toggle" style={{ marginTop: '20px', borderTop: '1px solid var(--border-light)', paddingTop: '20px' }}>
            <div>
              <h4>Enter is send</h4>
              <p>Enter key will send your message</p>
            </div>
            <ToggleSwitch checked={enterIsSend} onChange={() => toggleLocalSetting('settings_enter_send', enterIsSend, setEnterIsSend)} />
          </div>

          <div className="settings-row-select" style={{ marginTop: '20px', borderTop: '1px solid var(--border-light)', paddingTop: '20px', cursor: 'pointer' }} onClick={() => setView("chathistory")}>
            <div>
              <h4>Chat history</h4>
            </div>
            <FaChevronRight style={{ fontSize: '10px', color: '#888' }} />
          </div>
        </div>
      </div>
    );
  };

  const renderNotifications = () => (
    <div className="settings-subview">
      <div className="back-header">
        <FaArrowLeft className="back-icon" onClick={() => setView("main")} />
        <h2>Notifications</h2>
      </div>
      <div className="settings-content" style={{ padding: '20px' }}>
        <p style={{ color: '#8696a0', fontSize: '13px', paddingBottom: '10px', borderBottom: '1px solid var(--border-light)' }}>Messages</p>
        
        <div className="settings-row-toggle" style={{ marginTop: '20px' }}>
          <div>
            <h4>Sounds</h4>
            <p>Play sounds for incoming messages</p>
          </div>
          <ToggleSwitch checked={sounds} onChange={() => toggleLocalSetting('settings_sounds', sounds, setSounds)} />
        </div>

        <div className="settings-row-toggle" style={{ marginTop: '20px' }}>
          <div>
            <h4>Desktop alerts</h4>
            <p>Show desktop notifications for new messages</p>
          </div>
          <ToggleSwitch checked={desktopAlerts} onChange={() => toggleLocalSetting('settings_alerts', desktopAlerts, setDesktopAlerts)} />
        </div>

        <div className="settings-row-toggle" style={{ marginTop: '20px' }}>
          <div>
            <h4>Show previews</h4>
            <p>Display message text in desktop alerts</p>
          </div>
          <ToggleSwitch checked={showPreviews} onChange={() => toggleLocalSetting('settings_previews', showPreviews, setShowPreviews)} />
        </div>
      </div>
    </div>
  );

  const renderVideoVoice = () => (
    <div className="settings-subview">
      <div className="back-header">
        <FaArrowLeft className="back-icon" onClick={() => setView("main")} />
        <h2>Video & Voice</h2>
      </div>
      <div className="settings-content" style={{ padding: '20px' }}>
        <div className="settings-row-select">
          <div>
            <h4>Camera</h4>
            <p>Integrated Camera</p>
          </div>
          <select>
            <option>Integrated HD Webcam</option>
            <option>OBS Virtual Camera</option>
          </select>
        </div>

        <div className="settings-row-select" style={{ marginTop: '20px' }}>
          <div>
            <h4>Microphone</h4>
            <p>Default Microphone</p>
          </div>
          <select>
            <option>Default Realtek High Definition Audio</option>
            <option>External USB Microphone</option>
          </select>
        </div>

        <div className="settings-row-select" style={{ marginTop: '20px' }}>
          <div>
            <h4>Speakers</h4>
            <p>Default Speakers</p>
          </div>
          <select>
            <option>Speakers (Realtek High Definition Audio)</option>
            <option>Headphones (Bluetooth Audio)</option>
          </select>
        </div>

        <button 
          onClick={() => alert("Audio & Video Hardware Test Passed! Microphone input signal is active.")}
          style={{ width: '100%', marginTop: '30px', background: '#00a884', color: 'white', border: 'none', padding: '12px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
        >
          Test Microphone & Camera
        </button>
      </div>
    </div>
  );

  const renderShortcuts = () => (
    <div className="settings-subview">
      <div className="back-header">
        <FaArrowLeft className="back-icon" onClick={() => setView("main")} />
        <h2>Keyboard shortcuts</h2>
      </div>
      <div className="settings-content" style={{ padding: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '20px', fontSize: '14px', color: 'var(--text-primary)' }}>
          <span>Mark as unread</span><span style={{ color: '#8696a0' }}>Ctrl + Shift + U</span>
          <span>Archive chat</span><span style={{ color: '#8696a0' }}>Ctrl + Shift + E</span>
          <span>Pin chat</span><span style={{ color: '#8696a0' }}>Ctrl + Shift + P</span>
          <span>Search chat</span><span style={{ color: '#8696a0' }}>Ctrl + Shift + F</span>
          <span>New chat</span><span style={{ color: '#8696a0' }}>Ctrl + N</span>
          <span>Settings</span><span style={{ color: '#8696a0' }}>Ctrl + ,</span>
          <span>Mute chat</span><span style={{ color: '#8696a0' }}>Ctrl + Shift + M</span>
          <span>Delete chat</span><span style={{ color: '#8696a0' }}>Ctrl + Shift + D</span>
          <span>Search</span><span style={{ color: '#8696a0' }}>Ctrl + F</span>
          <span>New group</span><span style={{ color: '#8696a0' }}>Ctrl + Shift + N</span>
        </div>
      </div>
    </div>
  );

  const renderHelp = () => (
    <div className="settings-subview">
      <div className="back-header">
        <FaArrowLeft className="back-icon" onClick={() => setView("main")} />
        <h2>Help</h2>
      </div>
      <div className="settings-content">
        <SettingsItem icon={<FaQuestionCircle />} label="Help Centre" description="Get help, contact us" onClick={() => setView("help_centre")} />
        <SettingsItem icon={<FaFileContract />} label="Terms and Privacy Policy" onClick={() => setView("terms")} />
        <SettingsItem icon={<FaInfoCircle />} label="Channel reports" />
        
        <div style={{ marginTop: '40px', textAlign: 'center', paddingBottom: '40px' }}>
           <p style={{ color: '#8696a0', fontSize: '13px', letterSpacing: '0.5px' }}>
             WhatsApp Web Version 2.3000.1035<br />
             Bharat Connect Edition
           </p>
        </div>
      </div>
    </div>
  );

  const renderHelpCentre = () => (
    <div className="settings-subview">
      <div className="back-header">
        <FaArrowLeft className="back-icon" onClick={() => setView("help")} />
        <h2>Help Centre</h2>
      </div>
      <div className="settings-content" style={{ padding: '20px' }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <FaQuestionCircle style={{ fontSize: '60px', color: '#00a884', marginBottom: '15px' }} />
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '10px' }}>How can we help?</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Browse our FAQ or contact our support team directly.</p>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', color: '#00a884', fontSize: '13px', marginBottom: '8px', fontWeight: 'bold' }}>Tell us how we can help</label>
          <textarea 
            placeholder="Describe your issue..."
            style={{ width: '100%', height: '100px', background: 'var(--panel-bg)', border: '1px solid var(--border-light)', color: 'var(--text-primary)', padding: '12px', borderRadius: '8px', resize: 'none', outline: 'none', boxSizing: 'border-box' }}
          ></textarea>
        </div>

        <button 
          className="create-submit-btn"
          onClick={() => {
            alert("Support ticket sent! (Mock)");
            setView("help");
          }}
        >
          Send to Support
        </button>

        <div style={{ marginTop: '40px', borderTop: '1px solid var(--border-light)', paddingTop: '20px' }}>
          <h4 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>Popular Topics</h4>
          <p onClick={() => setView("faq_restore")} style={{ color: '#00a884', cursor: 'pointer', marginBottom: '10px', fontSize: '14px' }}>How to restore chat history</p>
          <p onClick={() => setView("faq_twostep")} style={{ color: '#00a884', cursor: 'pointer', marginBottom: '10px', fontSize: '14px' }}>About two-step verification</p>
          <p onClick={() => setView("faq_notifications")} style={{ color: '#00a884', cursor: 'pointer', marginBottom: '10px', fontSize: '14px' }}>How to manage your notifications</p>
        </div>
      </div>
    </div>
  );

  const renderFaqRestoreChat = () => (
    <div className="settings-subview">
      <div className="back-header">
        <FaArrowLeft className="back-icon" onClick={() => setView("help_centre")} />
        <h2>Restore Chat History</h2>
      </div>
      <div className="settings-content" style={{ padding: '20px' }}>
        <h3 style={{ color: 'var(--text-primary)', marginBottom: '20px' }}>How to restore your chat history</h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.6' }}>
          To restore your chat history, you must first ensure you have a backup stored in Google Drive or local storage.
          <br /><br />
          1. Uninstall and reinstall Bharat Connect.<br />
          2. Verify your phone number.<br />
          3. When prompted, tap <strong>RESTORE</strong> to restore your chats and media from Google Drive.<br />
          4. After the restoration process is complete, tap <strong>NEXT</strong>. Your chats will be displayed once initialization is complete.
        </p>
      </div>
    </div>
  );

  const renderFaqTwoStep = () => (
    <div className="settings-subview">
      <div className="back-header">
        <FaArrowLeft className="back-icon" onClick={() => setView("help_centre")} />
        <h2>Two-Step Verification</h2>
      </div>
      <div className="settings-content" style={{ padding: '20px' }}>
        <h3 style={{ color: 'var(--text-primary)', marginBottom: '20px' }}>About two-step verification</h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.6' }}>
          Two-step verification is an optional feature that adds more security to your Bharat Connect account. When you enable two-step verification, any attempt to verify your phone number on Bharat Connect must be accompanied by the six-digit PIN that you created using this feature.
          <br /><br />
          To enable two-step verification, open Bharat Connect &gt; <strong>Settings</strong> &gt; <strong>Account</strong> &gt; <strong>Two-step verification</strong> &gt; <strong>Enable</strong>.
        </p>
      </div>
    </div>
  );

  const renderFaqNotifications = () => (
    <div className="settings-subview">
      <div className="back-header">
        <FaArrowLeft className="back-icon" onClick={() => setView("help_centre")} />
        <h2>Manage Notifications</h2>
      </div>
      <div className="settings-content" style={{ padding: '20px' }}>
        <h3 style={{ color: 'var(--text-primary)', marginBottom: '20px' }}>How to manage your notifications</h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.6' }}>
          You can easily customize your notification preferences for messages and groups.
          <br /><br />
          To change your notification settings:
          <br />
          1. Open <strong>Settings</strong> &gt; <strong>Notifications</strong>.
          <br />
          2. You can enable or disable conversation tones, choose a notification tone, or adjust popup notifications and light colors.
          <br /><br />
          To mute a specific chat or group, open the chat, tap the contact or group name, and select <strong>Mute notifications</strong>.
        </p>
      </div>
    </div>
  );

  const renderTerms = () => (
    <div className="settings-subview">
      <div className="back-header">
        <FaArrowLeft className="back-icon" onClick={() => setView("help")} />
        <h2>Terms and Privacy</h2>
      </div>
      <div className="settings-content" style={{ padding: '20px' }}>
        <h3 style={{ color: 'var(--text-primary)', marginBottom: '20px' }}>Terms of Service</h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.6', marginBottom: '20px' }}>
          Welcome to Bharat Connect. These Terms of Service ("Terms") govern your use of the Bharat Connect platform, software, and services (collectively, the "Services"). By accessing or using our Services, you agree to be bound by these Terms.
          <br /><br />
          You must be at least 13 years old to use our Services. You are responsible for keeping your device and your Bharat Connect account safe and secure.
        </p>

        <h3 style={{ color: 'var(--text-primary)', marginBottom: '20px' }}>Privacy Policy</h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.6' }}>
          Respect for your privacy is coded into our DNA. Since we started Bharat Connect, we've aspired to build our Services with a set of strong privacy principles in mind.
          <br /><br />
          We do not retain your messages in the ordinary course of providing our Services to you. Once your messages (including your chats, photos, videos, voice messages, files, and share location information) are delivered, they are deleted from our servers. Your messages are stored on your own device.
        </p>
      </div>
    </div>
  );

  const renderGeneral = () => (
    <div className="settings-subview">
      <div className="back-header">
        <FaArrowLeft className="back-icon" onClick={() => setView("main")} />
        <h2>General</h2>
      </div>
      <div className="settings-content" style={{ padding: '20px' }}>
        <div className="settings-row-select">
          <div>
            <h4>Language</h4>
            <p>{lang}</p>
          </div>
          <select value={lang} onChange={(e) => handleChangeLanguage(e.target.value)}>
            <option value="English">English</option>
            <option value="Hindi">Hindi (हिंदी)</option>
            <option value="Spanish">Spanish (Español)</option>
            <option value="French">French (Français)</option>
            <option value="Gujarati">Gujarati (ગુજરાતી)</option>
            <option value="Marathi">Marathi (मराठी)</option>
          </select>
        </div>
        <div className="settings-row-toggle" style={{ marginTop: '20px' }}>
          <div>
            <h4>Start at login</h4>
            <p>Launch Bharat Connect automatically when computer starts</p>
          </div>
          <ToggleSwitch checked={sounds} onChange={() => toggleLocalSetting('settings_autostart', sounds, setSounds)} />
        </div>
        <div className="settings-row-toggle" style={{ marginTop: '20px' }}>
          <div>
            <h4>Typing indicator</h4>
            <p>Show when you are typing in chat</p>
          </div>
          <ToggleSwitch checked={true} onChange={() => {}} />
        </div>
      </div>
    </div>
  );

  const renderSecurity = () => (
    <div className="settings-subview">
      <div className="back-header">
        <FaArrowLeft className="back-icon" onClick={() => setView("account")} />
        <h2>Security notifications</h2>
      </div>
      <div className="settings-content" style={{ padding: '20px' }}>
        <p style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: 'bold', marginBottom: '10px' }}>
          Show security notifications on this computer
        </p>
        <p style={{ color: 'var(--text-secondary)', fontSize: '13.5px', lineHeight: '1.6', marginBottom: '20px' }}>
          Get notified when your security code changes for a contact's phone. If you have multiple devices, this setting must be enabled on each device where you want to receive notifications.
        </p>
        <div className="settings-row-toggle" style={{ borderTop: '1px solid var(--border-light)', paddingTop: '20px' }}>
          <div>
            <h4>Show security notifications</h4>
          </div>
          <ToggleSwitch 
            checked={sounds} 
            onChange={() => toggleLocalSetting('settings_sec_notif', sounds, setSounds)} 
          />
        </div>
      </div>
    </div>
  );

  const renderTwoStep = () => (
    <div className="settings-subview">
      <div className="back-header">
        <FaArrowLeft className="back-icon" onClick={() => setView("account")} />
        <h2>Two-step verification</h2>
      </div>
      <div className="settings-content" style={{ padding: '20px' }}>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.6', marginBottom: '20px' }}>
          For extra security, enable two-step verification to require a 6-digit PIN when registering your phone number with Bharat Connect again.
        </p>
        {isPinEnabled ? (
          <div style={{ background: 'rgba(0,168,132,0.1)', padding: '16px', borderRadius: '8px', border: '1px solid #00a884', marginBottom: '20px' }}>
            <h4 style={{ color: '#00a884', margin: '0 0 6px 0' }}>✓ Two-step verification is active</h4>
            <p style={{ color: 'var(--text-secondary)', fontSize: '13px', margin: 0 }}>Your account is protected with a 6-digit PIN.</p>
            <button 
              onClick={() => {
                setIsPinEnabled(false);
                localStorage.setItem("twostep_enabled", "false");
                alert("Two-step verification disabled.");
              }}
              style={{ marginTop: '14px', background: '#ef4444', color: 'white', border: 'none', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
            >
              Disable PIN
            </button>
          </div>
        ) : (
          <div>
            <label style={{ display: 'block', color: '#00a884', fontSize: '13px', marginBottom: '8px', fontWeight: 'bold' }}>Enter 6-digit PIN</label>
            <input 
              type="password" maxLength={6} placeholder="• • • • • •" value={pinCode}
              onChange={(e) => setPinCode(e.target.value)}
              style={{ width: '100%', padding: '12px', background: 'var(--search-bg)', border: '1px solid var(--border-light)', color: 'var(--text-primary)', borderRadius: '8px', outline: 'none', fontSize: '18px', letterSpacing: '4px', textAlign: 'center', marginBottom: '20px', boxSizing: 'border-box' }}
            />
            <button 
              onClick={handleSavePin}
              style={{ width: '100%', background: '#00a884', color: 'white', border: 'none', padding: '12px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
            >
              Enable Two-Step Verification
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderChangeNumber = () => (
    <div className="settings-subview">
      <div className="back-header">
        <FaArrowLeft className="back-icon" onClick={() => setView("account")} />
        <h2>Change number</h2>
      </div>
      <div className="settings-content" style={{ padding: '20px' }}>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.6', marginBottom: '20px' }}>
          Changing your phone number will migrate your account info, groups & settings.
        </p>
        <label style={{ display: 'block', color: '#00a884', fontSize: '13px', marginBottom: '6px', fontWeight: 'bold' }}>New Phone Number</label>
        <input 
          type="text" placeholder="+91 98765 43210" value={newPhone}
          onChange={(e) => setNewPhone(e.target.value)}
          style={{ width: '100%', padding: '12px', background: 'var(--search-bg)', border: '1px solid var(--border-light)', color: 'var(--text-primary)', borderRadius: '8px', outline: 'none', marginBottom: '20px', boxSizing: 'border-box' }}
        />
        <button 
          onClick={() => {
            if (!newPhone) { alert("Please enter a phone number!"); return; }
            alert(`Verification code sent to ${newPhone}!`);
            setView("account");
          }}
          style={{ width: '100%', background: '#00a884', color: 'white', border: 'none', padding: '12px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
        >
          Next
        </button>
      </div>
    </div>
  );

  const renderWallpaper = () => {
    const wallpapers = [
      { name: 'Dark Teal', color: '#0b141a' },
      { name: 'Midnight', color: '#111b21' },
      { name: 'Emerald', color: '#003e30' },
      { name: 'Deep Navy', color: '#0f172a' },
      { name: 'Charcoal', color: '#18181b' },
      { name: 'Royal Purple', color: '#2e1065' }
    ];

    return (
      <div className="settings-subview">
        <div className="back-header">
          <FaArrowLeft className="back-icon" onClick={() => setView("chats")} />
          <h2>Chat Wallpaper</h2>
        </div>
        <div className="settings-content" style={{ padding: '20px' }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '16px' }}>Choose chat background theme:</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
            {wallpapers.map((wp) => (
              <div 
                key={wp.color}
                onClick={() => handleSelectWallpaper(wp.color)}
                style={{ 
                  background: wp.color, 
                  height: '80px', 
                  borderRadius: '10px', 
                  border: selectedWallpaper === wp.color ? '3px solid #00a884' : '1px solid var(--border-light)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                {wp.name}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderChatHistory = () => (
    <div className="settings-subview">
      <div className="back-header">
        <FaArrowLeft className="back-icon" onClick={() => setView("chats")} />
        <h2>Chat history</h2>
      </div>
      <div className="settings-content">
        <SettingsItem icon={<FaArchive />} label="Archive all chats" onClick={() => alert("All chats archived!")} />
        <SettingsItem icon={<FaTrashAlt style={{color: '#f15c6d'}} />} label="Clear all messages" type="danger" onClick={() => alert("Messages cleared!")} />
        <SettingsItem icon={<FaTrashAlt style={{color: '#ef4444'}} />} label="Delete all chats" type="danger" onClick={() => alert("All chats deleted!")} />
      </div>
    </div>
  );

  return (
    <div className="settings-container" style={{ height: '100%', overflowY: 'auto' }}>
      {view === "main" && renderMain()}
      {view === "profile" && renderProfile()}
      {view === "account" && renderAccount()}
      {view === "privacy" && renderPrivacy()}
      {view === "chats" && renderChats()}
      {view === "video" && renderVideoVoice()}
      {view === "notifications" && renderNotifications()}
      {view === "shortcuts" && renderShortcuts()}
      {view === "help" && renderHelp()}
      {view === "help_centre" && renderHelpCentre()}
      {view === "terms" && renderTerms()}
      {view === "general" && renderGeneral()}
      {view === "faq_restore" && renderFaqRestoreChat()}
      {view === "faq_twostep" && renderFaqTwoStep()}
      {view === "faq_notifications" && renderFaqNotifications()}
      {view === "security" && renderSecurity()}
      {view === "twostep" && renderTwoStep()}
      {view === "changenumber" && renderChangeNumber()}
      {view === "wallpaper" && renderWallpaper()}
      {view === "chathistory" && renderChatHistory()}
    </div>
  );
}

export default SettingsList;
